import { invariant } from '@seen/core/invariant'
import type { GoalCarePlan } from '@seen/core/models/care-plans/GoalCarePlan'
import { CarePlanProblem } from '@seen/core/models/conditions/CarePlanProblem'
import { ExtendedCondition } from '@seen/core/models/conditions/ExtendedCondition'
import { hasReference } from '@seen/core/utils'
import { Badge, Button, type ButtonProps, Separator } from '@seen/ui'
import { type JSX, useMemo, useState } from 'react'
import { ConditionDisplay } from '#src/components/condition/ConditionDisplay.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogButton } from '#src/components/core/dialog/DialogButton.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export interface AddConditionButtonProps extends ButtonProps {
  carePlan: GoalCarePlan
  allGoalCarePlans: GoalCarePlan[]
}

export function AddConditionButton({
  carePlan,
  allGoalCarePlans,
  ...delegated
}: AddConditionButtonProps): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const practitionerRef = useCurrentPractitioner().reference

  const [open, setOpen] = useState(false)

  const addNewConditionMutation = useTransactionMutation()
  const addNewCondition = (): void => {
    addNewConditionMutation.mutate(async (transaction) => {
      const conditionRef = transaction.create(
        new CarePlanProblem({
          subject: carePlan.patientSubject,
          recorder: practitionerRef,
        })
      )
      transaction.patch(carePlan, (cp) => {
        cp.addCondition(conditionRef)
      })
    })
  }

  const addExistingConditionMutation = usePatchMutation<GoalCarePlan>()

  const addExistingCondition = (
    condition: ExtendedCondition | undefined
  ): void => {
    if (!condition) {
      return
    }
    addExistingConditionMutation.mutate({
      instance: carePlan,
      mutateFn: (cp) => {
        cp.addCondition(condition.reference)
      },
    })
  }

  const { data: conditions } = useSearch(
    ExtendedCondition,
    ExtendedCondition.searchQuery({
      subject: carePlan.patientSubject,
    })
  )

  const currentConditions = useMemo(
    () => conditions?.filter((condition) => condition.isCurrent) ?? [],
    [conditions]
  )

  const isExistingCondition = (condition: ExtendedCondition): boolean =>
    carePlan.conditions.some((c) => hasReference(c, condition.reference))

  const carePlanGoalsByCondition = useMemo(
    () =>
      allGoalCarePlans.reduce<Record<string, number[]>>((acc, gcp, idx) => {
        gcp.conditions.forEach((c) => {
          invariant(c.reference, 'Condition reference is required')
          if (!acc[c.reference]) {
            acc[c.reference] = [idx]
          } else {
            acc[c.reference] = [...acc[c.reference], idx]
          }
        })
        return acc
      }, {}),
    [allGoalCarePlans]
  )

  if (isReadOnly) {
    return null
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogButton {...delegated}>Add Problem</DialogButton>
      <DialogDisplay>
        <DialogHeader asChild>
          <DialogTitle>Add Problem</DialogTitle>
        </DialogHeader>
        <DialogContent className="gap-8 pt-2">
          <Button
            onClick={addNewCondition}
            loading={addNewConditionMutation.isPending}
            className="place-self-center"
          >
            Add New Problem
          </Button>
          {!!currentConditions.length && (
            <div className="grid gap-4">
              <Separator>Or add existing problem</Separator>
              {currentConditions.map((condition) => (
                <div
                  key={condition.id}
                  className="flex items-center justify-between"
                >
                  <ConditionDisplay condition={condition} />
                  <div className="flex items-center gap-2">
                    {carePlanGoalsByCondition[
                      condition.reference.reference
                    ]?.map((gcpIdx) => (
                      <Badge variant="outline" key={gcpIdx}>
                        Goal {gcpIdx + 1}
                      </Badge>
                    ))}
                    <Button
                      onClick={() => addExistingCondition(condition)}
                      loading={addExistingConditionMutation.isPending}
                      className="place-self-start"
                      variant="outline"
                      disabled={isExistingCondition(condition)}
                    >
                      {isExistingCondition(condition) ? 'Added' : 'Add'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </DialogDisplay>
    </Dialog>
  )
}
